import { useTranslation } from 'next-i18next'
import { FAQPageJsonLd, NextSeo } from 'next-seo'
import { makeStaticProps } from '../lib/getStatic'
import React, { useMemo } from 'react'
import HeroSection from '../components/index/HeroSection'
import ProofSection from '../components/index/ProofSection'
import BenefitsSection from '../components/index/BenefitsSection'
import StepsSection from '../components/index/StepsSection'
import TestimonialsSection from '../components/index/TestimonialsSection'
import FAQSection from '../components/index/FAQSection'
import BackgroundPicture from '../components/shared/BackgroundPicture'

const lightBackgroundSources = [
  {
    media: '(min-width: 768px)',
    srcSet: '/img/backgrounds/bg-light-desktop.webp',
    type: 'image/webp',
  },
  {
    media: '(min-width: 768px)',
    srcSet: '/img/backgrounds/bg-light-desktop.png',
    type: 'image/png',
  },
  {
    srcSet: '/img/backgrounds/bg-light-mobile.webp',
    type: 'image/webp',
  },
]

export default function HomePage() {
  const { t } = useTranslation('index')
  const faqJsonLd = useMemo(() => {
    const categories = t('landing.faq.categories', {
      returnObjects: true,
    })

    if (!Array.isArray(categories)) return []

    return categories.flatMap((category: any) =>
      Array.isArray(category.questions)
        ? category.questions
            .filter((item: any) => item?.question && item?.answer)
            .map((item: any) => ({
              questionName: item.question,
              acceptedAnswerText: item.answer,
            }))
        : [],
    )
  }, [t])

  const WebSiteJsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Shelters',
      description: t('seo.description'),
      alternateName: 'Shelters',
      url: process.env.NEXT_PUBLIC_BASEURL,
      sameAs: [
        process.env.NEXT_PUBLIC_FACEBOOK_PAGE,
        process.env.NEXT_PUBLIC_TWITTER_PAGE,
        process.env.NEXT_PUBLIC_INSTAGRAM_PAGE,
        process.env.NEXT_PUBLIC_LINKEDIN_PAGE,
      ],
    }),
    [t],
  )

  return (
    <>
      <NextSeo
        defaultTitle={t('seo.title') || ''}
        description={t('seo.description') || ''}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(WebSiteJsonLd) }}
      />
      {faqJsonLd.length > 0 && <FAQPageJsonLd mainEntity={faqJsonLd} />}
      <>
        <HeroSection />
        <div className="relative overflow-hidden py-12 tablet:py-32">
          <BackgroundPicture
            fallbackSrc="/img/backgrounds/bg-light-mobile.png"
            sources={lightBackgroundSources}
          />
          <div className="relative z-10 space-y-12 tablet:space-y-32">
            <ProofSection />
            <BenefitsSection />
          </div>
        </div>
        <StepsSection />
        <div className="relative overflow-hidden py-12 tablet:py-32">
          <BackgroundPicture
            fallbackSrc="/img/backgrounds/bg-light-mobile.png"
            sources={lightBackgroundSources}
          />
          <div className="relative z-10 space-y-12 tablet:space-y-32">
            <TestimonialsSection />
            <FAQSection />
          </div>
        </div>
      </>
    </>
  )
}

const getStaticProps = makeStaticProps([
  'common',
  'index',
  'navbar',
  'footer',
  'modals',
  'newsletter',
])
export { getStaticProps, HomePage }
